/**
 * CodeInput: entering a code someone reads from elsewhere (cockatrice-ui `src/ui/CodeInput.jsx`, ported to
 * hyperscript; PLAN.md §0).
 *
 * mode "otp" (default): a 6-digit one-time code from an authenticator app or email.
 *   One input, inputmode="numeric", autocomplete="one-time-code", so phones offer the code.
 * mode "key": a grouped key such as the recovery key (6 groups of 4).
 *   One box per group, monospace, upper-case. Typing moves on when a group is full,
 *   Backspace in an empty group goes back, and pasting the whole key fills every group.
 *
 * onChange(value) — otp: "123456"; key: "ABCD-EFGH-…" joined with `separator`.
 * onComplete(value) once every character is in.
 */
import './css/code-input.css';
import { createEffect, createSignal, createUniqueId, mergeProps, splitProps } from 'solid-js';
import { named, useFieldAttrs, wrapControl } from './Field.ts';
import { component, each, el, spread, when } from './solid.ts';
import type { Child, On } from './solid.ts';
import { uiText } from './text.ts';

export interface CodeInputProps {
  /** Visible label. When given (or help/error), the control wraps itself in a Field. */
  readonly label?: Child;
  /** Help text under the control, linked with aria-describedby. */
  readonly help?: Child;
  /** Error message. Sets aria-invalid and shows the message with a warning icon. */
  readonly error?: Child;
  /** Marks the field required and shows "Required". */
  readonly required?: boolean | undefined;
  /** Shows "Optional" next to the label. */
  readonly optional?: boolean | undefined;
  /** Class for the wrapping Field. */
  readonly fieldClass?: string | undefined;
  /** otp: one 6-digit box. key: grouped key such as the recovery key. */
  readonly mode?: 'otp' | 'key' | undefined;
  /** Digits in a one-time code. Default 6. */
  readonly length?: number | undefined;
  /**
   * otp only: cut a longer code to `length` (default true). false keeps every digit typed or pasted (no maxlength),
   * so the caller can refuse a code that is too long instead of sending its first `length` digits.
   */
  readonly cut?: boolean | undefined;
  /** Number of groups in a key. Default 6. */
  readonly groups?: number | undefined;
  /** Characters per group. Default 4. */
  readonly groupLength?: number | undefined;
  /** Allowed characters in a key. Default alnum (upper-cased). */
  readonly charset?: 'alnum' | 'digits' | undefined;
  /** Joins groups in the value. Default "-". */
  readonly separator?: string | undefined;
  /** Controlled value. */
  readonly value?: string | undefined;
  /** Uncontrolled starting value. */
  readonly defaultValue?: string | undefined;
  /** Called on every change. */
  readonly onChange?: ((value: string) => void) | undefined;
  /** Called once every character is in. */
  readonly onComplete?: ((value: string) => void) | undefined;
  /** Disables the input(s). */
  readonly disabled?: boolean | undefined;
  /** Name of the key group when there is no visible label. */
  readonly 'aria-label'?: string | undefined;
  /** Id of the (first) input. */
  readonly id?: string | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
  /** Ids of more text that describes the code (an error said elsewhere), after the Field's own. */
  readonly 'aria-describedby'?: string | undefined;
}

/** The inner inputs' props: the code's, the error styling and whether the Field shows a label. */
interface CodeBoxProps extends Omit<CodeInputProps, 'label' | 'help' | 'required' | 'optional' | 'fieldClass'> {
  readonly invalid?: boolean | undefined;
  readonly hasLabel?: boolean | undefined;
  readonly label?: string | undefined;
}

const FIELD_ONLY = ['label', 'help', 'optional', 'fieldClass', 'required'] as const;

export default function CodeInput(props: CodeInputProps): Child {
  const [, rest] = splitProps(props, FIELD_ONLY);
  return wrapControl(props, () =>
    when(
      () => props.mode === 'key',
      () =>
        component(
          KeyInput,
          mergeProps(named(rest), {
            get hasLabel() {
              return Boolean(props.label);
            },
          }),
        ),
      () => component(OtpInput, rest),
    ),
  );
}

function OtpInput(props: CodeBoxProps): Child {
  const p = mergeProps({ length: 6 }, props);
  const [local, rest] = splitProps(p, [
    'id',
    'mode',
    'length',
    'cut',
    'value',
    'defaultValue',
    'onChange',
    'onComplete',
    'error',
    'invalid',
    'disabled',
    'class',
    'aria-describedby',
  ]);
  const f = useFieldAttrs(local);
  const cut = (): boolean => local.cut !== false;
  const [val, setVal] = createSignal(local.value ?? local.defaultValue ?? '');
  createEffect(() => {
    if (local.value !== undefined) setVal(local.value);
  });

  return el(
    'input',
    spread(rest, {
      id: () => f.id(),
      class: () => ['ui-code-input', 'ui-code-input--otp', f.invalid() && 'is-invalid', local.class].filter(Boolean).join(' '),
      style: () => ({ '--len': local.length }),
      type: 'text',
      inputmode: 'numeric',
      autocomplete: 'one-time-code',
      pattern: () => `\\d{${local.length}}`,
      maxLength: () => (cut() ? local.length : undefined),
      spellcheck: false,
      placeholder: () => '·'.repeat(local.length),
      value: val,
      disabled: () => f.disabled(),
      'aria-invalid': () => (f.invalid() ? 'true' : undefined),
      'aria-describedby': () => f.describedBy(),
      onInput: (event: On<InputEvent, HTMLInputElement>) => {
        const digits = event.currentTarget.value.replace(/\D/g, '');
        const v = cut() ? digits.slice(0, local.length) : digits;
        event.currentTarget.value = v;
        setVal(v);
        local.onChange?.(v);
        if (v.length === local.length) local.onComplete?.(v);
      },
    }),
  );
}

function KeyInput(props: CodeBoxProps): Child {
  const p = mergeProps(
    {
      groups: 6,
      groupLength: 4,
      separator: '-',
      charset: 'alnum',
      get label() {
        return uiText()('ui.code-input.recovery-key');
      },
    } as const,
    props,
  );
  const [local, rest] = splitProps(p, [
    'id',
    'mode',
    'groups',
    'groupLength',
    'separator',
    'charset',
    'value',
    'defaultValue',
    'onChange',
    'onComplete',
    'error',
    'invalid',
    'disabled',
    'class',
    'hasLabel',
    'label',
    'aria-describedby',
    'aria-label',
  ]);
  const f = useFieldAttrs(local);
  const uid = createUniqueId();
  const clean = (s: string | undefined): string => {
    const up = (s ?? '').toUpperCase();
    return local.charset === 'digits' ? up.replace(/\D/g, '') : up.replace(/[^A-Z0-9]/g, '');
  };
  const split = (s: string | undefined): string[] => {
    const c = clean(s);
    return Array.from({ length: local.groups }, (_, i) => c.slice(i * local.groupLength, (i + 1) * local.groupLength));
  };
  const [parts, setParts] = createSignal(split(local.value ?? local.defaultValue));
  createEffect(() => {
    if (local.value !== undefined) setParts(split(local.value));
  });
  const inputs: (HTMLInputElement | undefined)[] = [];

  const emit = (next: string[]): void => {
    setParts(next);
    const joined = next.join(local.separator);
    local.onChange?.(joined);
    if (next.every((g) => g.length === local.groupLength)) local.onComplete?.(joined);
  };

  const fill = (start: number, text: string): void => {
    const next = split(parts().slice(0, start).join('') + clean(text));
    emit(next);
    const open = next.findIndex((g) => g.length < local.groupLength);
    queueMicrotask(() => inputs[open === -1 ? local.groups - 1 : open]?.focus());
  };
  const slots = (): number[] => Array.from({ length: local.groups }, (_, n) => n);

  return el(
    'div',
    {
      class: () => ['ui-code-input--key', f.invalid() && 'is-invalid', f.disabled() && 'is-disabled', local.class].filter(Boolean).join(' '),
      role: 'group',
      'aria-label': () => (local.hasLabel === true ? undefined : (local['aria-label'] ?? local.label)),
      'aria-describedby': () => f.describedBy(),
    },
    each(slots, (n) => [
      when(
        () => n > 0,
        () => el('span', { class: 'ui-code-input__sep', 'aria-hidden': 'true' }, '–'),
      ),
      el(
        'input',
        spread(rest, {
          ref: (node: HTMLInputElement) => {
            inputs[n] = node;
          },
          id: () => (n === 0 ? f.id() : `${uid}-${n}`),
          class: 'ui-code-input ui-code-input__group',
          style: () => ({ '--len': local.groupLength }),
          type: 'text',
          inputmode: () => (local.charset === 'digits' ? 'numeric' : 'text'),
          autocomplete: 'off',
          autocapitalize: 'characters',
          spellcheck: false,
          maxLength: () => local.groupLength * local.groups,
          'aria-label': () => uiText()('ui.code-input.group', { n: n + 1, total: local.groups }),
          'aria-invalid': () => (f.invalid() ? 'true' : undefined),
          disabled: () => f.disabled(),
          value: () => parts()[n],
          onInput: (event: On<InputEvent, HTMLInputElement>) => {
            const v = event.currentTarget.value;
            if (clean(v).length > local.groupLength) {
              fill(n, v);
              return;
            }
            const next = [...parts()];
            const group = clean(v);
            next[n] = group;
            event.currentTarget.value = group;
            emit(next);
            if (group.length === local.groupLength && n < local.groups - 1) inputs[n + 1]?.focus();
          },
          onPaste: (event: ClipboardEvent) => {
            const text = event.clipboardData?.getData('text') ?? '';
            if (!text) return;
            event.preventDefault();
            fill(n, text);
          },
          onKeyDown: (event: On<KeyboardEvent, HTMLInputElement>) => {
            const box = event.currentTarget;
            if (event.key === 'Backspace' && box.value === '' && n > 0) {
              event.preventDefault();
              inputs[n - 1]?.focus();
            } else if (event.key === 'ArrowLeft' && box.selectionStart === 0 && n > 0) {
              event.preventDefault();
              inputs[n - 1]?.focus();
            } else if (event.key === 'ArrowRight' && box.selectionStart === box.value.length && n < local.groups - 1) {
              event.preventDefault();
              inputs[n + 1]?.focus();
            }
          },
          onFocus: (event: On<FocusEvent, HTMLInputElement>) => {
            event.currentTarget.select();
          },
        }),
      ),
    ]),
  );
}
